import { FunctionComponent } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import type { ManagementCapaHourPoint } from "../utils/management-capa-chart.utils";
import {
  getManagementChartLegendProps,
  getManagementChartTooltipProps
} from "../utils/management-chart-theme.utils";
import {
  formatManagementChartTooltipValue,
  getManagementPeriodChartYAxisProps
} from "../utils/management-display-unit.utils";
import { ManagementChartEmptyState } from "./ManagementChartEmptyState";

type ManagementCapaHourChartProps = {
  data: ManagementCapaHourPoint[];
  primarySeriesName?: string;
  compareSeriesName?: string;
  /** Idle / empty copy when `data` has no points. */
  emptyMessageKey?: string;
};

/**
 * Capacity-by-hour bars for the Management location detail page.
 */
export const ManagementCapaHourChart: FunctionComponent<ManagementCapaHourChartProps> = ({
  data,
  primarySeriesName,
  compareSeriesName,
  emptyMessageKey = "_management_capaIdle"
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tooltipProps = getManagementChartTooltipProps(theme);
  const legendProps = getManagementChartLegendProps(theme);
  const yAxisProps = getManagementPeriodChartYAxisProps("percent");
  const primaryName = primarySeriesName ?? t("Occupancy");
  const showCompare = Boolean(compareSeriesName);

  return (
    <Box data-testid="management-capa-hour-chart" sx={{ width: "100%", height: 240 }}>
      <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {t("_management_capaPerHour")}
      </Typography>
      {data.length === 0 ? (
        <ManagementChartEmptyState messageKey={emptyMessageKey} />
      ) : (
        <ResponsiveContainer width="100%" height="90%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              dataKey="hourLabel"
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            />
            <YAxis
              domain={yAxisProps.domain}
              allowDecimals={yAxisProps.allowDecimals}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              tickFormatter={yAxisProps.tickFormatter}
            />
            <Tooltip
              contentStyle={tooltipProps.contentStyle}
              itemStyle={tooltipProps.itemStyle}
              labelStyle={tooltipProps.labelStyle}
              formatter={(value: number) => formatManagementChartTooltipValue(value, "percent")}
            />
            {showCompare && (
              <Legend wrapperStyle={legendProps.wrapperStyle} formatter={legendProps.formatter} />
            )}
            <Bar dataKey="display" name={primaryName} fill={theme.palette.info.main} />
            {showCompare && (
              <Bar
                dataKey="displayCompare"
                name={compareSeriesName}
                fill={theme.palette.warning.main}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};
